import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { abTestingService } from '../../../services/abTestingService';

const rate = (value) => `${Number(value || 0).toFixed(1)}%`;

const TestDetailModal = ({ test, onClose, onUpdate }) => {
  const [saving, setSaving] = useState(false);
  const isFormExperiment = test?.testKind === 'form';
  const shareLink = typeof window !== 'undefined' ? `${window.location.origin}/experiments/${test?.id}` : '';

  const leader = useMemo(() => {
    const eligible = (test?.variants || [])
      .filter((variant) => Number(variant?.metrics?.viewCount || 0) > 0)
      .sort((a, b) => {
        const rateDifference = Number(b?.metrics?.conversionRate || 0) - Number(a?.metrics?.conversionRate || 0);
        return rateDifference || Number(b?.metrics?.submissionCount || 0) - Number(a?.metrics?.submissionCount || 0);
      });
    if (eligible.length < 2) return eligible[0] || null;
    if (Number(eligible[0]?.metrics?.conversionRate || 0) === Number(eligible[1]?.metrics?.conversionRate || 0)) return null;
    return eligible[0];
  }, [test?.variants]);

  const declaredWinner = test?.variants?.find((variant) => variant.id === test?.winnerVariantId);

  const declareWinner = async (variantId) => {
    if (!window.confirm('Declare this form as the winner and complete the experiment?')) return;
    try {
      setSaving(true);
      await abTestingService.updateTest(test.id, {
        status: 'completed',
        winnerVariantId: variantId,
        endDate: new Date().toISOString(),
      });
      await onUpdate();
      onClose();
    } catch (err) {
      window.alert(err?.message || 'Could not declare the winner.');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      window.prompt('Copy this experiment link:', shareLink);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl border border-border bg-card shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card p-6">
          <div>
            <h2 className="text-xl font-bold text-foreground">{test?.testName}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{test?.description || (isFormExperiment ? 'Form comparison experiment' : 'Archived email A/B test')}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-muted" aria-label="Close">
            <Icon name="X" size={20} />
          </button>
        </div>

        {isFormExperiment ? (
          <div className="p-6">
            <div className="mb-6 flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:flex-row sm:items-center">
              <Icon name="Link" size={20} className="text-primary" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">Share this experiment link</p>
                <p className="truncate text-xs text-muted-foreground">{shareLink}</p>
              </div>
              <Button variant="outline" onClick={copyLink}>
                <Icon name="Copy" size={16} />
                Copy link
              </Button>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ['Total views', test?.summary?.views || 0, 'Eye'],
                ['Total clicks', test?.summary?.clicks || 0, 'MousePointerClick'],
                ['Submissions', test?.summary?.submissions || 0, 'Send'],
                ['Conversion', rate(test?.summary?.conversionRate), 'Target'],
              ].map(([label, value, icon]) => (
                <div key={label} className="rounded-lg border border-border p-4">
                  <Icon name={icon} size={18} className="mb-2 text-primary" />
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 text-xl font-bold text-foreground">{value}</p>
                </div>
              ))}
            </div>

            {declaredWinner ? (
              <div className="mb-5 flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 p-4 text-green-900">
                <Icon name="Trophy" size={20} />
                <p><strong>{declaredWinner?.form?.title || declaredWinner?.variantName}</strong> is the declared winning form.</p>
              </div>
            ) : leader ? (
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center gap-3">
                  <Icon name="TrendingUp" size={20} className="text-primary" />
                  <p className="text-sm text-foreground"><strong>{leader?.form?.title || leader?.variantName}</strong> is currently leading on conversion rate.</p>
                </div>
                {test?.status === 'active' && (
                  <Button onClick={() => declareWinner(leader.id)} disabled={saving}>
                    <Icon name={saving ? 'Loader2' : 'Trophy'} size={16} className={saving ? 'animate-spin' : ''} />
                    Declare winner
                  </Button>
                )}
              </div>
            ) : (
              <p className="mb-5 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">A current leader will appear after visitors view both forms and one achieves a higher conversion rate.</p>
            )}

            <h3 className="mb-4 text-lg font-semibold text-foreground">Per-form performance</h3>
            <div className="grid gap-4 md:grid-cols-2">
              {(test?.variants || []).map((variant) => {
                const isWinner = variant.id === test?.winnerVariantId;
                return (
                  <section key={variant.id} className={`rounded-xl border p-5 ${isWinner ? 'border-green-400 bg-green-50/40' : 'border-border'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-semibold text-foreground">{variant?.form?.title || variant?.variantName}</h4>
                          {variant?.isControl && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">Control</span>}
                          {isWinner && <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">Winner</span>}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{variant?.trafficPercentage}% configured traffic</p>
                      </div>
                      {test?.status === 'active' && !isWinner && (
                        <Button variant="outline" onClick={() => declareWinner(variant.id)} disabled={saving}>Choose winner</Button>
                      )}
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      {[
                        ['Views', variant?.metrics?.viewCount || 0],
                        ['Clicks', variant?.metrics?.clickCount || 0],
                        ['Submissions', variant?.metrics?.submissionCount || 0],
                        ['Conversion', rate(variant?.metrics?.conversionRate)],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-lg bg-muted/50 p-3">
                          <p className="text-xs text-muted-foreground">{label}</p>
                          <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="rounded-xl border border-border bg-muted/40 p-5">
              <div className="flex gap-3">
                <Icon name="Archive" size={20} className="mt-0.5 text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-foreground">Archived email A/B test</h3>
                  <p className="mt-1 text-sm text-muted-foreground">This record is preserved from the retired Email Automations feature. New A/B tests compare published web forms instead.</p>
                </div>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {(test?.variants || []).map((variant) => (
                  <div key={variant.id} className="rounded-lg border border-border bg-card p-4">
                    <p className="font-medium text-foreground">{variant?.variantName}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{variant?.subjectLine || 'Historic variant'}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end border-t border-border p-6">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default TestDetailModal;